'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import toast from 'react-hot-toast';
import Breadcrumb from '@/components/Breadcrumbs/Breadcrumb';
import InputGroup from '@/components/FormElements/InputGroup';
import { ShowcaseSection } from '@/components/Layouts/showcase-section';
import { Select } from '@/components/FormElements/select';
import { StaysSearchForm, StaySearchParams } from '@/components/Stays';
import {
  Booking,
  BookingStatus,
  BookingPaymentStatus,
  BOOKING_STATUSES,
  BOOKING_PAYMENT_STATUSES,
} from '@/lib/bookings/types';
import {
  getBookings,
  deleteBooking,
  updateBookingStatus,
  deleteBookingsBulk,
  updateBookingsStatusBulk,
} from '@/lib/bookings/api';

type ModuleType = 'stay' | 'trip';
type TabType = 'all' | ModuleType;

const TABS: { label: string; value: TabType }[] = [
  { label: 'All Bookings', value: 'all' },
  { label: 'Stays', value: 'stay' },
  { label: 'Trips', value: 'trip' },
];

export default function BookingsPage() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [staysSearchParams, setStaysSearchParams] = useState<StaySearchParams>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkStatusMenuOpen, setBulkStatusMenuOpen] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, [page, search, statusFilter, paymentStatusFilter, activeTab]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const params: any = {
        page,
        limit: 20,
        sort_by: 'created_at',
        sort_dir: 'DESC',
      };

      // Note: moduleType filtering is done client-side since API doesn't support it
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (paymentStatusFilter) params.paymentStatus = paymentStatusFilter;

      const response = await getBookings(params);
      if (response) {
        setBookings(response.data || []);
        setTotalPages(response.meta?.total_pages || 1);
        setTotalRecords(response.meta?.total_records || 0);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Determine booking type from items
  const getBookingType = (booking: Booking): 'stay' | 'trip' => {
    const resourceName = booking.items?.[0]?.resourceName?.toLowerCase() || '';
    const isStay = resourceName.match(/hostel|hotel|resort|lodge|apartment|room|marina|boutique|seaside|mountain/);
    return isStay ? 'stay' : 'trip';
  };

  // Filter bookings based on active tab (since API doesn't support moduleType)
  const filteredBookings = bookings.filter((b) => {
    if (activeTab === 'all') return true;
    const type = getBookingType(b);
    return type === activeTab;
  });

  const handleDelete = async (bookingId: string) => {
    if (!confirm('Are you sure you want to delete this booking?')) return;

    try {
      setDeleteLoading(bookingId);
      await deleteBooking(bookingId);
      setBookings(bookings.filter((b) => b.id !== bookingId));
      setTotalRecords(totalRecords - 1);
      toast.success('Booking deleted successfully');
    } catch (error: any) {
      console.error('Error deleting booking:', error);
      const errorMsg = error?.response?.data?.message || 'Failed to delete booking';
      toast.error(errorMsg);
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleStatusChange = async (bookingId: string, newStatus: BookingStatus) => {
    try {
      setStatusLoading(bookingId);
      await updateBookingStatus(bookingId, newStatus);
      setBookings(
        bookings.map((b) =>
          b.id === bookingId ? { ...b, status: newStatus } : b
        )
      );
      toast.success(`Booking status updated to ${newStatus}`);
    } catch (error: any) {
      console.error('Error updating booking status:', error);
      const errorMsg = error?.response?.data?.message || 'Failed to update booking status';
      toast.error(errorMsg);
    } finally {
      setStatusLoading(null);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(bookings.map((b) => b.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (bookingId: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(bookingId);
    } else {
      newSelected.delete(bookingId);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} booking(s)?`)) return;

    try {
      setBulkActionLoading(true);
      await deleteBookingsBulk(Array.from(selectedIds));
      setBookings(bookings.filter((b) => !selectedIds.has(b.id)));
      setTotalRecords(totalRecords - selectedIds.size);
      setSelectedIds(new Set());
      toast.success('Selected bookings deleted successfully');
    } catch (error: any) {
      console.error('Error bulk deleting bookings:', error);
      const errorMsg = error?.response?.data?.message || 'Failed to delete bookings';
      toast.error(errorMsg);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkStatusChange = async (newStatus: BookingStatus) => {
    if (selectedIds.size === 0) return;

    try {
      setBulkActionLoading(true);
      setBulkStatusMenuOpen(false);
      await updateBookingsStatusBulk(Array.from(selectedIds), newStatus);
      setBookings(
        bookings.map((b) =>
          selectedIds.has(b.id) ? { ...b, status: newStatus } : b
        )
      );
      setSelectedIds(new Set());
      toast.success(`Selected bookings updated to ${newStatus}`);
    } catch (error: any) {
      console.error('Error bulk updating bookings:', error);
      const errorMsg = error?.response?.data?.message || 'Failed to update bookings';
      toast.error(errorMsg);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchBookings();
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handlePaymentStatusFilterChange = (value: string) => {
    setPaymentStatusFilter(value);
    setPage(1);
  };

  const getStatusColor = (status: BookingStatus) => {
    const statusConfig = BOOKING_STATUSES.find((s) => s.value === status);
    switch (statusConfig?.color) {
      case 'green':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'yellow':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'red':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'blue':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getPaymentStatusColor = (status: BookingPaymentStatus) => {
    const statusConfig = BOOKING_PAYMENT_STATUSES.find((s) => s.value === status);
    switch (statusConfig?.color) {
      case 'green':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'yellow':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'red':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getModuleBadge = (moduleType?: string) => {
    if (!moduleType) return null;
    const colors: Record<string, string> = {
      stay: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
      trip: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
    };
    return (
      <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${colors[moduleType] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`}>
        {moduleType === 'stay' ? '🏨 Stay' : '✈️ Trip'}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const confirmedBookings = filteredBookings.filter(
    (b) => b.status === BookingStatus.CONFIRMED
  ).length;
  const pendingBookings = filteredBookings.filter(
    (b) => b.status === BookingStatus.PENDING
  ).length;
  // totalAmount comes as string from API, convert to number
  const totalRevenue = filteredBookings.reduce((sum, b) => sum + Number(b.totalAmount || 0), 0);

  // Count stays and trips based on items resourceName
  const stayBookings = bookings.filter((b) =>
    b.items?.some((item) => item.resourceName?.toLowerCase().includes('hostel') || 
                            item.resourceName?.toLowerCase().includes('hotel') || 
                            item.resourceName?.toLowerCase().includes('resort') ||
                            item.resourceName?.toLowerCase().includes('lodge') ||
                            item.resourceName?.toLowerCase().includes('apartment') ||
                            item.resourceName?.toLowerCase().includes('room'))
  ).length;
  const tripBookings = bookings.filter((b) =>
    b.items?.some((item) => !item.resourceName?.toLowerCase().match(/hostel|hotel|resort|lodge|apartment|room/))
  ).length;

  return (
    <div className="space-y-6">
      <Breadcrumb pageName="Booking Management" />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-body text-gray-600 dark:text-gray-400">
            Manage bookings, reservations, and trips
          </p>
        </div>
        <Link href="/bookings/create">
          <button className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-7.5 py-3 text-center font-medium text-white hover:bg-opacity-90 transition duration-200 ease-in-out">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create New Booking
          </button>
        </Link>
      </div>

      {/* Module Type Tabs */}
      <div className="border-b border-stroke dark:border-dark-3">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setActiveTab(tab.value);
                setPage(1);
              }}
              className={`relative pb-4 text-sm font-medium transition-colors ${
                activeTab === tab.value
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
              {activeTab === tab.value && (
                <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                  {tab.value === 'all' ? totalRecords : tab.value === 'stay' ? stayBookings : tripBookings}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'stay' ? (
        <StaysSearchForm
          onSearch={(params) => {
            setStaysSearchParams(params);
            setPage(1);
          }}
        />
      ) : (
        <ShowcaseSection title="Search & Filter" className="!p-5.5">
          <form onSubmit={handleSearch} className="flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-3">
            <div className="flex-1">
              <InputGroup
                label=""
                placeholder="Search bookings by number, customer..."
                type="text"
                value={search}
                handleChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                label=""
                items={[{ label: 'All Statuses', value: '' }, ...BOOKING_STATUSES]}
                placeholder="Filter by status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                label=""
                items={[{ label: 'All Payments', value: '' }, ...BOOKING_PAYMENT_STATUSES]}
                placeholder="Filter by payment"
                value={paymentStatusFilter}
                onChange={handlePaymentStatusFilterChange}
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center rounded-lg bg-primary px-6 py-3 text-center font-medium text-white hover:bg-opacity-90 transition duration-200 ease-in-out h-11.5"
            >
              Search
            </button>
          </form>
        </ShowcaseSection>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <ShowcaseSection title="Total Bookings" className="!p-5.5">
          <p className="text-4xl font-bold text-primary">{totalRecords}</p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">All bookings in system</p>
        </ShowcaseSection>
        <ShowcaseSection title="Confirmed" className="!p-5.5">
          <p className="text-4xl font-bold text-green-600">{confirmedBookings}</p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Confirmed reservations</p>
        </ShowcaseSection>
        <ShowcaseSection title="Pending" className="!p-5.5">
          <p className="text-4xl font-bold text-yellow-600">{pendingBookings}</p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Awaiting confirmation</p>
        </ShowcaseSection>
        <ShowcaseSection title="Total Revenue" className="!p-5.5">
          <p className="text-4xl font-bold text-blue-600">{formatCurrency(totalRevenue)}</p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">From displayed bookings</p>
        </ShowcaseSection>
      </div>

      {selectedIds.size > 0 && (
        <div className="sticky bottom-6 z-50 flex items-center justify-between rounded-lg border border-stroke bg-white px-4 py-3 shadow-lg dark:border-dark-3 dark:bg-dark">
          <div className="flex items-center gap-3">
            <button
              onClick={clearSelection}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕ Clear
            </button>
            <span className="text-sm font-medium text-dark dark:text-white">
              {selectedIds.size} booking{selectedIds.size > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-3">
            {/* Status dropdown */}
            <div className="relative">
              <button
                onClick={() => setBulkStatusMenuOpen(!bulkStatusMenuOpen)}
                disabled={bulkActionLoading}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-stroke px-4 py-2 text-sm font-medium text-dark hover:bg-gray-2 disabled:opacity-50 dark:border-dark-3 dark:text-white dark:hover:bg-dark-2"
              >
                📝 Change Status
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {bulkStatusMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-lg border border-stroke bg-white shadow-lg dark:border-dark-3 dark:bg-dark">
                  {BOOKING_STATUSES.map((status) => (
                    <button
                      key={status.value}
                      onClick={() => handleBulkStatusChange(status.value as BookingStatus)}
                      className="block w-full text-left px-4 py-2 text-sm text-dark hover:bg-gray-2 dark:text-white dark:hover:bg-dark-2"
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handleBulkDelete}
              disabled={bulkActionLoading}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              🗑️ Delete
            </button>
          </div>
        </div>
      )}

      <ShowcaseSection title="Bookings List" className="overflow-hidden !p-0">
        <table className="w-full">
          <thead className="border-b border-stroke bg-gray-2 dark:border-dark-3 dark:bg-dark">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white w-12">
                <input
                  type="checkbox"
                  checked={bookings.length > 0 && selectedIds.size === filteredBookings.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="h-4 w-4 cursor-pointer rounded border-gray-4 text-primary focus:ring-primary"
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">#</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">Booking #</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">Customer</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">Type</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white min-w-[120px]">Status</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white min-w-[140px]">Payment</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">Dates</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">Amount</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">PNR</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-dark dark:text-white">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center">
                  <p className="text-body text-gray-600 dark:text-gray-400">Loading bookings...</p>
                </td>
              </tr>
            ) : filteredBookings.length > 0 ? (
              filteredBookings.map((booking, index) => (
                <tr
                  key={booking.id}
                  className={`border-b border-stroke hover:bg-gray-2 dark:border-dark-3 dark:hover:bg-dark-2 ${
                    selectedIds.has(booking.id) ? 'bg-primary/5 dark:bg-primary/10' : ''
                  }`}
                >
                  <td className="px-4 py-3 text-sm text-dark-6 dark:text-gray-5 w-12">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(booking.id)}
                      onChange={(e) => handleSelectOne(booking.id, e.target.checked)}
                      className="h-4 w-4 cursor-pointer rounded border-gray-4 text-primary focus:ring-primary"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-dark-6 dark:text-gray-5">
                    {(page - 1) * 20 + index + 1}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-dark dark:text-white">
                    {booking.bookingNumber}
                  </td>
                  <td className="px-4 py-3 text-sm text-dark dark:text-white">
                    {booking.customer ? `${booking.customer.firstName} ${booking.customer.lastName}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {getBookingType(booking) === 'stay' ? (
                      <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
                        🏨 Stay
                      </span>
                    ) : (
                      <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-800 dark:bg-orange-900/20 dark:text-orange-400">
                        ✈️ Trip
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm min-w-[120px]">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(
                        booking.status
                      )}`}
                    >
                      {booking.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm min-w-[140px]">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${getPaymentStatusColor(
                        booking.paymentStatus
                      )}`}
                    >
                      {booking.paymentStatus.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-dark-6 dark:text-gray-5">
                    {booking.startDate ? formatDate(booking.startDate) : '-'} - {booking.endDate ? formatDate(booking.endDate) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-dark dark:text-white">
                    {formatCurrency(booking.totalAmount)}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-dark-6 dark:text-gray-5">
                    {booking.bookingNumber}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center space-x-3">
                      <Link href={`/bookings/${booking.id}`}>
                        <button className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors dark:bg-primary/20 dark:text-primary">
                          👁️
                        </button>
                      </Link>
                      <button
                        onClick={() => handleDelete(booking.id)}
                        disabled={deleteLoading === booking.id}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-red-600/10 text-red-600 hover:bg-red-600/20 transition-colors disabled:opacity-50 dark:bg-red-600/20 dark:text-red-400"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center">
                  <p className="text-body text-gray-600 dark:text-gray-400">
                    No bookings found. Create one to get started.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </ShowcaseSection>

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-6">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="inline-flex rounded-lg border border-stroke px-6 py-3 font-medium text-dark hover:bg-gray-2 disabled:opacity-50 dark:border-dark-3 dark:text-white dark:hover:bg-dark"
          >
            ← Previous
          </button>
          <span className="text-body font-medium text-dark dark:text-white">
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            className="inline-flex rounded-lg border border-stroke px-6 py-3 font-medium text-dark hover:bg-gray-2 disabled:opacity-50 dark:border-dark-3 dark:text-white dark:hover:bg-dark"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
